import { useMemo } from 'react'

import {
  AIChatSuggestedPrompt,
  CreateAIChatMutation,
  CreateAIChatMutationVariables,
  GetStaffAIChatSuggestedPromptsQuery,
  GetStaffAIChatSuggestedPromptsQueryVariables,
  UpdateAIChatMutation,
  UpdateAIChatMutationVariables,
} from 'types/graphql'

import { QueryKey, useMutation, useQuery } from 'src/utils/queryClient'

/**
 * Mutation to create a staff AI chat message.
 * Uses the unified createAIChat endpoint with sessionType: Internal.
 */
const CreateStaffAIChat = gql`
  mutation CreateAIChatMutation($input: CreateAIChatInput!) {
    createAIChat(input: $input) {
      id
      response
      questionCreatedAt
      responseCreatedAt
      citations {
        ...AIChatCitationFragment
      }
    }
  }
`

export const useCreateStaffAIChatMutation = () => {
  return useMutation<CreateAIChatMutation, CreateAIChatMutationVariables>({
    mutationDocument: CreateStaffAIChat,
  })
}

/**
 * Mutation to rate a staff AI chat message (thumbs up/down).
 */
const UpdateStaffAIChat = gql`
  mutation UpdateAIChatMutation($id: Int!, $input: UpdateAIChatInput!) {
    updateAIChat(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateStaffAIChatMutation = () => {
  return useMutation<UpdateAIChatMutation, UpdateAIChatMutationVariables>({
    mutationDocument: UpdateStaffAIChat,
  })
}

/**
 * Query to get personalized prompt suggestions for staff AI chat.
 */
const GetStaffAIChatSuggestedPrompts = gql`
  query GetStaffAIChatSuggestedPromptsQuery {
    getAIChatSuggestedPrompts(sessionType: Internal) {
      prompt
      category
    }
  }
`

export const useGetStaffAIChatSuggestedPromptsQuery = () => {
  const { data, ...rest } = useQuery<
    GetStaffAIChatSuggestedPromptsQuery,
    GetStaffAIChatSuggestedPromptsQueryVariables
  >({
    queryDocument: GetStaffAIChatSuggestedPrompts,
    queryKey: [QueryKey.StaffAIChatSuggestedPrompts],
    variables: {},
  })

  const suggestedPrompts: AIChatSuggestedPrompt[] = useMemo(
    () => data?.getAIChatSuggestedPrompts ?? [],
    [data]
  )

  return {
    ...rest,
    suggestedPrompts,
  }
}
